import React, { useState, useCallback, useEffect } from 'react';
import { auditApi, staffApi } from '../api/client';
import { Spin, Empty } from '../components/UI';
import { BarChart2 } from '../components/Icons';

const ACTION_COLORS = {
  CREATE: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  UPDATE: { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  DELETE: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  LOGIN:  { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
  CHANGE_PASSWORD: { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  RECORD: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
};

const fmtDT = s => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('en-IN', { dateStyle: 'medium' }) + ' ' +
         d.toLocaleTimeString('en-IN', { timeStyle: 'short', hour12: true });
};

export default function AuditTrail() {
  const [rows, setRows]     = useState([]);
  const [busy, setBusy]     = useState(false);
  const [staff, setStaff]   = useState([]);
  const [filters, setFilters] = useState({ entity_type: '', action: '', user_id: '', page: 1 });
  const setF = k => e => setFilters(f => ({ ...f, [k]: e.target.value, page: 1 }));

  useEffect(() => {
    staffApi.list().then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page_size: 100 };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action)      params.action      = filters.action;
      if (filters.user_id)     params.user_id     = filters.user_id;
      const { data } = await auditApi.list(params);
      setRows(data);
    } catch { setRows([]); }
    finally { setBusy(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const ENTITY_TYPES = ['Customer', 'Sale', 'Bill', 'Complaint', 'Service', 'Staff', 'Inventory', 'Expense', 'Auth', 'Template'];
  const ACTIONS      = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'CHANGE_PASSWORD', 'RECORD'];

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-hd"><div className="card-title"><BarChart2 s={15}/>Audit Trail Filters</div></div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 160 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>Module</label>
              <select value={filters.entity_type} onChange={setF('entity_type')}>
                <option value="">All Modules</option>
                {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 150 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>Action</label>
              <select value={filters.action} onChange={setF('action')}>
                <option value="">All Actions</option>
                {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 180 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>Staff Member</label>
              <select value={filters.user_id} onChange={setF('user_id')}>
                <option value="">All Staff</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={load} disabled={busy} style={{ whiteSpace: 'nowrap' }}>
              {busy ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary strip */}
      {rows.length > 0 && !busy && (
        <div className="notice n-info" style={{ marginBottom: 14, fontSize: 13 }}>
          Showing <strong>{rows.length}</strong> audit records
          {filters.entity_type && ` for module: ${filters.entity_type}`}
          {filters.action      && ` | action: ${filters.action}`}
          {filters.user_id     && ` | filtered by staff`}
        </div>
      )}

      <div className="card card-0">
        {busy ? <Spin label="Loading audit logs…"/> : rows.length === 0 ? (
          <Empty msg="No audit records found"/>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Date &amp; Time</th>
                  <th>Staff</th>
                  <th style={{ width: 80 }}>Role</th>
                  <th style={{ width: 90 }}>Action</th>
                  <th style={{ width: 100 }}>Module</th>
                  <th style={{ width: 120 }}>Reference</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const ac = ACTION_COLORS[r.action] || ACTION_COLORS.RECORD;
                  return (
                    <tr key={r.id}>
                      <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtDT(r.created_at)}</td>
                      <td style={{ fontWeight: 500 }}>{r.user_name || '—'}</td>
                      <td style={{ fontSize: 11 }}><span className="badge b-gray">{r.user_role || '—'}</span></td>
                      <td>
                        <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: ac.bg, color: ac.color, border: `1px solid ${ac.border}`, fontWeight: 600 }}>
                          {r.action}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{r.entity_type}</td>
                      <td style={{ fontSize: 12, color: 'var(--navy)', fontFamily: 'monospace' }}>{r.entity_ref || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{r.description || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
